import { CenterLine } from './centerline';
import { Chunk } from './chunks';
import { CHUNK_SIZE } from './grid';
import { Level, PAD_BLEND } from './level';
import { hash2 } from './noise';

/**
 * Rocks and stones, placed deterministically per chunk. The sim collides with exactly these and the view draws
 * exactly these (src/view/scatter.ts reads this list), so what the rider sees is what the bike hits.
 */
export interface ScatterItem { x: number; y: number; z: number; yaw: number; scale: number; model: number }

/**
 * Model footprints at scale 1 (m): half extents along the model's own x and z, and height. Measured from
 * public/assets/nature/rocks.glb (rock07..rock13, lod0) and stone.glb.
 */
export const SCATTER_MODELS: readonly { name: string; hx: number; hz: number; h: number }[] = [
  { name: 'rock07', hx: 0.85, hz: 0.6, h: 0.74 },
  { name: 'rock08', hx: 0.6, hz: 0.49, h: 0.53 },
  { name: 'rock09', hx: 0.81, hz: 0.51, h: 0.6 },
  { name: 'rock10', hx: 1.1, hz: 0.78, h: 0.77 },
  { name: 'rock11', hx: 0.98, hz: 0.76, h: 1.31 },
  { name: 'rock12', hx: 0.79, hz: 0.72, h: 0.74 },
  { name: 'rock13', hx: 1.05, hz: 0.57, h: 1.26 },
  { name: 'stone', hx: 0.073, hz: 0.044, h: 0.07 },
];
export const ROCK_MODEL_COUNT = 7;
export const STONE_MODEL = 7;
/** Rocks sink this fraction of their scale into the ground (as drawn). */
export const SCATTER_SINK = 0.08;

const INDEX_CELL = 16;
const lineIndex = new WeakMap<CenterLine, Map<number, number[]>>();

/**
 * |distance| from (x, z) to the line if it is under `within`, else Infinity. A grid of the line's vertices keeps it
 * cheap (a chunk places ~500 items; a full `closest` per item was a 170 ms streaming hitch).
 */
export function lineDistance(line: CenterLine, x: number, z: number, within: number): number {
  let grid = lineIndex.get(line);
  if (!grid) {
    grid = new Map();
    for (let k = 0; k < line.count; k++) {
      const key = Math.floor(line.xs[k]! / INDEX_CELL) * 65536 + Math.floor(line.zs[k]! / INDEX_CELL);
      let l = grid.get(key); if (!l) { l = []; grid.set(key, l); } l.push(k);
    }
    lineIndex.set(line, grid);
  }
  const r = within + 2 * line.step;
  let best = -1, bd = Infinity;
  for (let i = Math.floor((x - r) / INDEX_CELL); i <= Math.floor((x + r) / INDEX_CELL); i++) {
    for (let j = Math.floor((z - r) / INDEX_CELL); j <= Math.floor((z + r) / INDEX_CELL); j++) {
      const l = grid.get(i * 65536 + j);
      if (!l) continue;
      for (const k of l) { const d = Math.hypot(line.xs[k]! - x, line.zs[k]! - z); if (d < bd) { bd = d; best = k; } }
    }
  }
  if (best < 0 || bd > r) return Infinity;
  const d = Math.abs(line.closest(x, z, best * line.step, 4).d);
  return d < within ? d : Infinity;
}

/** Every rock and stone whose centre is inside chunk c. */
export function scatterChunk(level: Level, c: Chunk): ScatterItem[] {
  const out: ScatterItem[] = [];
  const seed = level.def.seed + 500;
  const line = level.line;
  const ox = c.ci * CHUNK_SIZE, oz = c.cj * CHUNK_SIZE;
  const hw = level.halfWidth, edge = hw + level.blend;
  // Nothing lands where people ride: not on the lap, a training loop or a graded pad (paddock, drill lanes); the
  // pad ring and the woodland boulders also keep clear of the lap's verges.
  const clear = (x: number, z: number, verge: boolean) => {
    if (level.padSurface(x, z)) return false;
    for (const p of level.paths) if (lineDistance(p.line, x, z, p.half + 0.5) < Infinity) return false;
    if (!line) return true;
    return lineDistance(line, x, z, verge ? hw + 0.3 : edge) === Infinity;
  };
  const put = (x: number, z: number, model: number, scale: number, k: number, verge = false) => {
    if (x < ox || z < oz || x >= ox + CHUNK_SIZE || z >= oz + CHUNK_SIZE) return;
    if (!clear(x, z, verge)) return;
    out.push({ x, z, y: c.field.heightAt(x, z) - scale * SCATTER_SINK, yaw: hash2(seed + 9, k, c.ci * 7 + c.cj) * 6.28, scale, model });
  };
  if (line) {
    // Along both verges: rocks every ~20 m (forest) or a few big ones (track); stones every few metres.
    const forest = level.def.forest !== undefined;
    for (let k = 0; k < line.count; k++) {
      const x = line.xs[k]!, z = line.zs[k]!;
      if (x < ox - 20 || z < oz - 20 || x > ox + CHUNK_SIZE + 20 || z > oz + CHUNK_SIZE + 20) continue;
      const k1 = (k + 1) % line.count;
      let tx = line.xs[k1]! - x, tz = line.zs[k1]! - z;
      const l = Math.hypot(tx, tz) || 1; tx /= l; tz /= l;
      for (const side of [-1, 1]) {
        if (hash2(seed + 1, k, side) < (forest ? 0.07 : 0.012)) {
          const off = edge + 1.5 + 5 * hash2(seed + 2, k, side);
          put(x - tz * off * side, z + tx * off * side, Math.floor(hash2(seed + 3, k, side) * ROCK_MODEL_COUNT), 0.5 + 0.9 * hash2(seed + 4, k, side), k * 2 + side, true);
        }
        if (hash2(seed + 5, k, side) < 0.5) {
          const off = hw + 0.6 + (edge - hw + 2) * hash2(seed + 6, k, side);
          put(x - tz * off * side, z + tx * off * side, STONE_MODEL, 1.2 + 2.5 * hash2(seed + 7, k, side), k * 2 + side + 100000, true);
        }
      }
    }
  }
  const pad = level.def.pads[0];
  if (pad) {
    for (let k = 0; k < 400; k++) {
      const a = hash2(seed + 11, k, 0) * Math.PI * 2, r = pad.radius + 1 + (PAD_BLEND + 6) * hash2(seed + 12, k, 0);
      const big = hash2(seed + 13, k, 0) < 0.1;
      put(pad.x + Math.cos(a) * r, pad.z + Math.sin(a) * r, big ? Math.floor(hash2(seed + 14, k, 0) * ROCK_MODEL_COUNT) : STONE_MODEL,
        big ? 0.4 + 0.6 * hash2(seed + 15, k, 0) : 1.5 + 2 * hash2(seed + 15, k, 0), k);
    }
  }
  if (c.trees) {
    // Woodland boulders between the trees.
    for (let k = 0; k < 90; k++) {
      const x = ox + hash2(seed + 21, k, c.ci) * CHUNK_SIZE, z = oz + hash2(seed + 22, k, c.cj) * CHUNK_SIZE;
      put(x, z, Math.floor(hash2(seed + 23, k, 0) * ROCK_MODEL_COUNT), 0.5 + 1.3 * hash2(seed + 24, k, 0), k + 500);
    }
  }
  return out;
}
